/**
 * Authentication action creators
 * This module contains functions for handling user authentication
 * @module Auth/Actions
 */
import { store } from '../../../redux/store';
import { login, resetTabsVoice } from './reducer';
import { flushLocalData, setLocalData } from '../../../utils/storage';
import { services } from '../../../api';
import { LoginDataType, LoginForm } from '../../../interfaces/api/auth/login';
import { keys } from '../../../api/keys';
import { finishLoading, startLoading } from '../../../connected-components/loading/actions';
import { tr } from '../../../translation';
import { toast } from 'sonner';

/**
 * Authenticates a user with the provided credentials
 * Handles login process, error handling, and success notifications
 *
 * @param {LoginForm} body - The user's login credentials
 * @param {boolean} [hideMessage] - Optional flag to suppress success notification
 * @returns {Promise<{ success: boolean; message?: string }>} Object indicating login success or failure
 */
export const logIn = async (
  body: LoginForm,
  hideMessage?: boolean,
): Promise<{ success: boolean; message?: string }> => {
  try {
    startLoading();

    const res = await services.auth.login(body);
    if (!res.data) {
      throw new Error('');
    }
    await startSession(res?.data);
    if (!hideMessage) {
      toast.success(tr('login.loginSuccessMessage'));
    }
    return { success: true };
  } catch (error: any) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('LOGIN FAIL: ', error);
    }
    toast.error('Login failed. Please check your credentials and try again.');
    const message = error.response?.data?.error?.message
      ? error.response?.data?.error?.message
      : tr('login.loginFailMessage');
    return { success: false, message };
  } finally {
    finishLoading();
  }
};

/**
 * Starts a user session after successful authentication
 * Stores user data in local storage and updates Redux state
 *
 * @param {LoginDataType} user - The authenticated user data
 * @returns {Promise<boolean>} True if session started successfully, false otherwise
 */
export const startSession = async (user: LoginDataType) => {
  try {
    const currentUser = { ...user };
    await setLocalData(keys.CURRENT_USER, currentUser);
    store.dispatch(login(currentUser));
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('LOGIN FAIL: ', error);
    }
    return false;
  }
};

/**
 * Ends the current user session (logout)
 * Clears user data, resets application state, and shows success notification
 *
 * @returns {Promise<boolean>} - A promise that resolves to `true` if logout is successful, otherwise `false`.
 */
export const endSession = async () => {
  try {
    startLoading();
    store.dispatch(resetTabsVoice());
    const { currentUser } = store.getState().auth || {};
    if (currentUser?.access_token) {
      toast.success(tr('login.logoutSuccessfully'));
    }
    await startSession({
      ...currentUser,
      access_token: null,
    });
    flushLocalData(keys.CHAT_CATEGORIES);
    store.dispatch({ type: 'logout', payload: null });
    return true;
  } catch (error) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('LOGOUT FAIL: ', error);
    }
    return false;
  } finally {
    finishLoading();
  }
};
